import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable } from 'rxjs';
import {
  ConversationViewDto,
  FREEZE_LABELS,
  MODE_LABELS,
  OutreachConversationRowDto,
  SimScriptDto,
  SimScriptsDto,
  SimStepDto,
  SimStepViewDto,
  outreachError,
} from '../../../data-transfer-objects/outreach.dto';
import { OutreachService } from '../../../services/outreach.service';
import { ConversationViewComponent } from '../conversation-view/conversation-view.component';
import {
  ActionConfirmData,
  ActionConfirmDialogComponent,
  ActionConfirmResult,
} from '../../abuse/action-confirm-dialog/action-confirm-dialog.component';

/**
 * Play the member (QA only). The simulator runs the production turn against
 * a made-up member: same context, same brain, same checks, same state
 * machine, no Firestore and no phone. The conversation on the left is the
 * same component the real conversation page uses, so an assisted draft is
 * sent, edited, rejected and learned from exactly as it would be for real.
 * The right column is what Sharon decided on the last step, and why.
 */
@Component({
  selector: 'app-outreach-simulator',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatProgressSpinnerModule, ConversationViewComponent],
  templateUrl: './outreach-simulator.component.html',
  styleUrls: ['./outreach-simulator.component.scss'],
})
export class OutreachSimulatorComponent implements OnInit {
  readonly modeLabels = MODE_LABELS;

  setup: SimScriptsDto | null = null;
  sims: OutreachConversationRowDto[] = [];
  loading = true;
  error: string | null = null;
  busy: string | null = null;

  personaId = '';
  cause = 'feedback';
  mode: 'assisted' | 'auto' = 'auto';

  simId: number | null = null;
  simView: ConversationViewDto | null = null;
  step: SimStepDto | null = null;
  memberText = '';
  /** Per simulated conversation, how many messages of each script were played. */
  private played = new Map<number, Record<string, number>>();

  constructor(private outreach: OutreachService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.outreach.simScripts().subscribe({
      next: (s) => {
        this.setup = s;
        this.personaId = s.personas[0]?.id ?? '';
        this.cause = s.causes[0]?.id ?? 'feedback';
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = outreachError(err, 'Could not load the simulator');
      },
    });
    this.loadSims();
  }

  loadSims(): void {
    this.outreach.simSessions().subscribe({
      next: ({ conversations }) => (this.sims = conversations),
      error: () => undefined,
    });
  }

  start(): void {
    if (!this.personaId || this.busy) return;
    this.run('start', this.outreach.simCreate({ personaId: this.personaId, cause: this.cause, mode: this.mode }), () =>
      this.loadSims()
    );
  }

  reopen(id: number): void {
    if (this.busy) return;
    this.simId = id;
    this.simView = null;
    this.step = null;
    this.error = null;
  }

  onViewChange(v: ConversationViewDto): void {
    this.simView = v;
  }

  sendMember(text = this.memberText): void {
    const t = text.trim();
    if (!t || !this.simId || this.busy) return;
    this.run('member', this.outreach.simMemberMessage(this.simId, t), () => {
      if (t === this.memberText.trim()) this.memberText = '';
    });
  }

  /** Scripts are played one message per click. */
  progress(script: SimScriptDto): number {
    return (this.simId && this.played.get(this.simId)?.[script.id]) || 0;
  }

  playScript(script: SimScriptDto): void {
    const i = this.progress(script);
    if (!this.simId || i >= script.messages.length || this.busy) return;
    const id = this.simId;
    this.run('member', this.outreach.simMemberMessage(id, script.messages[i]), () => {
      this.played.set(id, { ...(this.played.get(id) ?? {}), [script.id]: i + 1 });
    });
  }

  advance(): void {
    if (this.simId) this.run('advance', this.outreach.simAdvance(this.simId));
  }

  reset(): void {
    if (!this.simId) return;
    const id = this.simId;
    this.run('reset', this.outreach.simReset(id), () => this.played.delete(id));
  }

  remove(): void {
    if (!this.simId || this.busy) return;
    const id = this.simId;
    this.dialog
      .open<ActionConfirmDialogComponent, ActionConfirmData, ActionConfirmResult>(ActionConfirmDialogComponent, {
        width: '480px',
        maxWidth: '96vw',
        data: {
          title: 'Delete this simulated conversation?',
          description: 'The made-up member, the turns, drafts and intel of this run are deleted.',
          confirmLabel: 'Delete',
          danger: true,
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (!res?.confirmed) return;
        this.busy = 'delete';
        this.outreach.simDelete(id).subscribe({
          next: () => {
            this.busy = null;
            this.simId = null;
            this.simView = null;
            this.step = null;
            this.played.delete(id);
            this.sims = this.sims.filter((s) => s.id !== id);
          },
          error: (err) => {
            this.busy = null;
            this.error = outreachError(err, 'Could not delete');
          },
        });
      });
  }

  freezeLabel(reason: string | null | undefined): string {
    return reason ? FREEZE_LABELS[reason] ?? reason : '';
  }

  private run(name: string, call: Observable<SimStepViewDto>, after?: () => void): void {
    this.busy = name;
    this.error = null;
    call.subscribe({
      next: ({ step, ...view }) => {
        this.busy = null;
        this.simId = step.conversationId;
        this.simView = view;
        this.step = step;
        after?.();
      },
      error: (err) => {
        this.busy = null;
        this.error = outreachError(err, 'The simulator failed');
      },
    });
  }
}
